import { getConferenceName } from '../base/conference/functions';
import { AI_NOTES_DEFAULT_POLL_INTERVAL_MS, AI_NOTES_DEFAULT_SERVICE_URL, AI_NOTES_FEATURE_KEY, AI_NOTES_HTTP_CONFLICT, AI_NOTES_HTTP_NOT_FOUND, AI_NOTES_PATH_ASSISTANT_CHAT, AI_NOTES_PATH_FINALIZE, AI_NOTES_PATH_RECORDINGS, AI_NOTES_PATH_SUMMARIZE_PARTIAL, AI_NOTES_PATH_SUMMARY, AI_NOTES_PATH_SUMMARY_TIMELINE, AI_NOTES_SUMMARY_MODE_FINAL, AI_NOTES_SUMMARY_MODE_PARTIAL, MS_PER_SECOND, REC_SUMMARY_POLL_INTERVAL_MS, REC_SUMMARY_POLL_MAX_ATTEMPTS, REC_SUMMARY_STATUS_FAILED, REC_SUMMARY_STATUS_PENDING, REC_SUMMARY_STATUS_PROCESSING, REC_SUMMARY_STATUS_READY } from './constants';
import logger from './logger';
/**
 * AI 服务 HTTP 错误（携带状态码）。
 */
export class MeetingAiApiError extends Error {
    status;
    /**
     * @param {number} status - HTTP 状态码。
     * @param {string} message - 错误说明。
     */
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = 'MeetingAiApiError';
    }
}
/**
 * Whether AI Notes UI is enabled via config.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {boolean}
 */
export function isAiNotesEnabled(state) {
    return state['features/base/config']?.aiNotes?.enabled === true;
}
/**
 * AI 纪要侧栏是否打开.
 *
 * @param {IReduxState} state - State.
 * @returns {boolean}
 */
export function isAiNotesPanelVisible(state) {
    return Boolean(state[AI_NOTES_FEATURE_KEY]?.panelVisible);
}
/**
 * 下次自动总结剩余毫秒（已过期为 0）.
 *
 * @param {IReduxState} state - State.
 * @returns {number | null} null 表示未在倒计时.
 */
export function getAiNotesAutoSummaryRemainingMs(state) {
    const at = state[AI_NOTES_FEATURE_KEY]?.nextSummaryAt;
    if (typeof at !== 'number') {
        return null;
    }
    return Math.max(0, at - Date.now());
}
/**
 * 是否本机回环主机名。
 *
 * @param {string} hostname - 主机名。
 * @returns {boolean}
 */
function isLoopbackHostname(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}
/**
 * Meeting AI service base URL from config.
 * 真机访问局域网 IP 时，config 里的 127.0.0.1 会指向手机自己；HTTPS 页请求 HTTP 也会被混合内容拦截。
 * 这两种情况改写为同源 /meeting-ai，由 webpack/nginx 反代到 meeting-ai-service。
 *
 * @param {IReduxState} state - Redux state.
 * @returns {string}
 */
export function getAiNotesServiceUrl(state) {
    const url = state['features/base/config']?.aiNotes?.serviceUrl;
    const configured = typeof url === 'string' && url.trim()
        ? url.replace(/\/$/, '')
        : AI_NOTES_DEFAULT_SERVICE_URL;
    if (configured.startsWith('/') || typeof window === 'undefined') {
        return configured;
    }
    try {
        const parsed = new URL(configured);
        const pageHost = window.location.hostname;
        const mixedContent = window.location.protocol === 'https:' && parsed.protocol === 'http:';
        const loopbackOnLan = isLoopbackHostname(parsed.hostname) && !isLoopbackHostname(pageHost);
        if (mixedContent || loopbackOnLan) {
            logger.info(`AI 服务地址改写为同源反代 | from=${configured} to=/meeting-ai`
                + ` pageHost=${pageHost} mixed=${mixedContent} loopbackOnLan=${loopbackOnLan}`);
            return '/meeting-ai';
        }
    }
    catch {
        return configured;
    }
    return configured;
}
/**
 * Poll interval for partial summaries.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {number}
 */
export function getAiNotesPollIntervalMs(state) {
    const value = state['features/base/config']?.aiNotes?.pollIntervalMs;
    if (typeof value === 'number' && value > 0) {
        return value;
    }
    return AI_NOTES_DEFAULT_POLL_INTERVAL_MS;
}
/**
 * Meeting id used by meeting-ai-service (room local name).
 *
 * @param {IReduxState} state - Redux state.
 * @returns {string}
 */
export function getAiNotesMeetingId(state) {
    return getConferenceName(state) || 'unknown-meeting';
}
/**
 * Map API JSON to IAiNotesSummary.
 *
 * @param {any} data - Raw API body.
 * @returns {IAiNotesSummary}
 */
export function mapSummaryResponse(data) {
    return {
        overview: data?.overview ?? null,
        decisions: Array.isArray(data?.decisions) ? data.decisions.map(String) : [],
        actionItems: Array.isArray(data?.action_items)
            ? data.action_items.map((item) => ({
                owner: String(item?.owner ?? ''),
                task: String(item?.task ?? ''),
                due: String(item?.due ?? '')
            }))
            : [],
        chapters: Array.isArray(data?.chapters)
            ? data.chapters.map((item) => ({
                title: String(item?.title ?? ''),
                summary: String(item?.summary ?? '')
            }))
            : [],
        speakers: Array.isArray(data?.speakers) ? data.speakers.map(String) : [],
        riskPoints: Array.isArray(data?.risk_points) ? data.risk_points.map(String) : [],
        status: data?.status,
        summaryMode: data?.summary_mode ?? null,
        modelName: data?.model_name ?? null,
        errorMessage: data?.error_message ?? null,
        updatedAt: data?.updated_at ?? null
    };
}
/**
 * POST summarize-partial.
 *
 * @param {IStore} store - Redux store.
 * @param {{ force?: boolean }} [options] - force 绕过防抖.
 * @returns {Promise<IAiNotesSummary | null>}
 */
export async function requestPartialSummary(store, options) {
    const state = store.getState();
    const base = getAiNotesServiceUrl(state);
    const meetingId = encodeURIComponent(getAiNotesMeetingId(state));
    const force = Boolean(options?.force);
    const forceQuery = force ? '?force=true' : '';
    const url = `${base}${AI_NOTES_PATH_SUMMARY}/${meetingId}/${AI_NOTES_PATH_SUMMARIZE_PARTIAL}${forceQuery}`;
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    const body = await response.json();
    if (body?.skipped && !body?.summary) {
        return null;
    }
    return body?.summary ? mapSummaryResponse(body.summary) : null;
}
/**
 * GET summary.
 *
 * @param {IStore} store - Redux store.
 * @param {string} mode - partial | final.
 * @returns {Promise<IAiNotesSummary>}
 */
export async function fetchSummary(store, mode) {
    const state = store.getState();
    const base = getAiNotesServiceUrl(state);
    const meetingId = encodeURIComponent(getAiNotesMeetingId(state));
    const url = `${base}${AI_NOTES_PATH_SUMMARY}/${meetingId}/summary?mode=${encodeURIComponent(mode)}`;
    const response = await fetch(url);
    if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `HTTP ${response.status}`);
    }
    return mapSummaryResponse(await response.json());
}
/**
 * POST finalize.
 *
 * @param {IStore} store - Redux store.
 * @returns {Promise<IAiNotesSummary>}
 */
export async function requestFinalizeSummary(store) {
    const state = store.getState();
    const base = getAiNotesServiceUrl(state);
    const meetingId = encodeURIComponent(getAiNotesMeetingId(state));
    const url = `${base}${AI_NOTES_PATH_SUMMARY}/${meetingId}/${AI_NOTES_PATH_FINALIZE}`;
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `HTTP ${response.status}`);
    }
    const body = await response.json();
    return mapSummaryResponse(body?.summary ?? body);
}
/**
 * 按会议号拉取纪要（门户录制页等非会中场景）。
 * 优先 final，未就绪则回退已有纪要（含 partial）。
 *
 * @param {string} serviceUrl - meeting-ai 基址（可为 /meeting-ai）。
 * @param {string} meetingId - 会议号。
 * @returns {Promise<IAiNotesSummary>}
 */
export async function fetchMeetingSummaryById(serviceUrl, meetingId) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const meetingPath = encodeURIComponent(meetingId);
    const finalUrl = `${base}${AI_NOTES_PATH_SUMMARY}/${meetingPath}/summary`
        + `?mode=${encodeURIComponent(AI_NOTES_SUMMARY_MODE_FINAL)}`;
    let response = await fetch(finalUrl, { method: 'GET' });
    if (response.status === AI_NOTES_HTTP_NOT_FOUND) {
        response = await fetch(`${base}${AI_NOTES_PATH_SUMMARY}/${meetingPath}/summary`, { method: 'GET' });
    }
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    return mapSummaryResponse(await response.json());
}
/**
 * 按会议号拉取会中侧栏时间线（只读，不触发重新生成）。
 * 404 / 无条目时返回空数组。
 *
 * @param {string} serviceUrl - meeting-ai 基址。
 * @param {string} meetingId - 会议号或房间名。
 * @returns {Promise<IAiNotesSummary[]>}
 */
export async function fetchMeetingSummaryTimeline(serviceUrl, meetingId) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const url = `${base}${AI_NOTES_PATH_SUMMARY}/${encodeURIComponent(meetingId)}`
        + `/${AI_NOTES_PATH_SUMMARY_TIMELINE}`;
    const response = await fetch(url, { method: 'GET' });
    if (response.status === AI_NOTES_HTTP_NOT_FOUND) {
        return [];
    }
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    const data = await response.json();
    const items = Array.isArray(data?.items) ? data.items : [];
    return items.map(mapSummaryResponse);
}
/**
 * 按会议号生成总纪要；响应体已含 summary，避免二次 GET。
 *
 * @param {string} serviceUrl - meeting-ai 基址。
 * @param {string} meetingId - 会议号。
 * @returns {Promise<IAiNotesSummary>}
 */
export async function finalizeMeetingSummaryById(serviceUrl, meetingId) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const url = `${base}${AI_NOTES_PATH_SUMMARY}/${encodeURIComponent(meetingId)}/${AI_NOTES_PATH_FINALIZE}`;
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    const body = await response.json();
    return mapSummaryResponse(body?.summary ?? body);
}
/**
 * 查询录制会议总结（按 Portal recording_id）。
 *
 * @param {string} serviceUrl - meeting-ai 基址。
 * @param {number|string} recordingId - 录制 ID。
 * @returns {Promise<IAiNotesSummary>}
 */
export async function fetchRecordingSummaryById(serviceUrl, recordingId) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const url = `${base}${AI_NOTES_PATH_RECORDINGS}/${encodeURIComponent(String(recordingId))}/summary`;
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    const data = await response.json();
    return mapSummaryResponse({
        ...data,
        meeting_id: String(data?.recording_id ?? recordingId)
    });
}
/**
 * 触发录制会议总结生成（幂等）。
 *
 * @param {string} serviceUrl - meeting-ai 基址。
 * @param {number|string} recordingId - 录制 ID。
 * @returns {Promise<IAiNotesSummary>}
 */
export async function generateRecordingSummaryById(serviceUrl, recordingId) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const url = `${base}${AI_NOTES_PATH_RECORDINGS}/${encodeURIComponent(String(recordingId))}/generate`;
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    const body = await response.json();
    const data = body?.summary ?? body;
    return mapSummaryResponse({
        ...data,
        meeting_id: String(data?.recording_id ?? recordingId)
    });
}
/**
 * 强制重新生成录制会议总结。
 *
 * @param {string} serviceUrl - meeting-ai 基址。
 * @param {number|string} recordingId - 录制 ID。
 * @returns {Promise<IAiNotesSummary>}
 */
export async function regenerateRecordingSummaryById(serviceUrl, recordingId) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const url = `${base}${AI_NOTES_PATH_RECORDINGS}/${encodeURIComponent(String(recordingId))}/regenerate`;
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    const body = await response.json();
    const data = body?.summary ?? body;
    return mapSummaryResponse({
        ...data,
        meeting_id: String(data?.recording_id ?? recordingId)
    });
}
/**
 * 拉取录制全文逐字稿。
 *
 * @param {string} serviceUrl - meeting-ai 基址。
 * @param {number|string} recordingId - 录制 ID。
 * @returns {Promise<IRecordingTranscriptChunk[]>}
 */
export async function fetchRecordingTranscriptById(serviceUrl, recordingId) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const url = `${base}${AI_NOTES_PATH_RECORDINGS}/${encodeURIComponent(String(recordingId))}/transcript`;
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, detail || `HTTP ${response.status}`);
    }
    const body = await response.json();
    const items = Array.isArray(body?.items) ? body.items : [];
    return items.map((item) => ({
        chunkId: String(item?.chunk_id ?? ''),
        displayName: String(item?.display_name ?? ''),
        endMs: Number(item?.end_ms ?? 0),
        participantId: String(item?.participant_id ?? ''),
        startMs: Number(item?.start_ms ?? 0),
        text: String(item?.text ?? '')
    }));
}
/**
 * 解析 FastAPI / SSE 错误正文。
 *
 * @param {string} detail - 原始响应体。
 * @param {number} status - HTTP 状态码。
 * @returns {string}
 */
function parseAssistantError(detail, status) {
    let message = detail || `HTTP ${status}`;
    try {
        const parsed = JSON.parse(detail);
        if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
            return parsed.detail;
        }
        if (Array.isArray(parsed?.detail) && parsed.detail[0]?.msg) {
            return String(parsed.detail[0].msg);
        }
        if (typeof parsed?.error === 'string' && parsed.error.trim()) {
            return parsed.error;
        }
    }
    catch {
        // 非 JSON 错误体，沿用原文
    }
    return message;
}
/**
 * 门户 AI 小助手：SSE 流式调用 meeting-ai 已配置的 LLM。
 *
 * @param {string} serviceUrl - meeting-ai 基址。
 * @param {IAssistantChatMessage[]} messages - 不含 system 的对话历史，最后一条须为 user。
 * @param {Function} onDelta - 每段增量回调。
 * @param {AbortSignal} [signal] - 取消信号。
 * @returns {Promise<string>} 完整回答。
 */
export async function streamAssistantChat(serviceUrl, messages, onDelta, signal) {
    const base = (serviceUrl || AI_NOTES_DEFAULT_SERVICE_URL).replace(/\/+$/, '');
    const url = `${base}${AI_NOTES_PATH_ASSISTANT_CHAT}`;
    const response = await fetch(url, {
        body: JSON.stringify({ messages }),
        headers: {
            Accept: 'text/event-stream',
            'Content-Type': 'application/json'
        },
        method: 'POST',
        signal
    });
    if (!response.ok) {
        const detail = await response.text();
        throw new MeetingAiApiError(response.status, parseAssistantError(detail, response.status));
    }
    if (!response.body) {
        throw new MeetingAiApiError(response.status, 'empty stream');
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let full = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
            const line = part.split('\n').find(item => item.startsWith('data: '));
            if (!line) {
                continue;
            }
            const raw = line.slice(6).trim();
            if (!raw) {
                continue;
            }
            let payload = {};
            try {
                payload = JSON.parse(raw);
            }
            catch {
                continue;
            }
            if (payload.error) {
                throw new MeetingAiApiError(502, payload.error);
            }
            if (typeof payload.delta === 'string' && payload.delta) {
                full += payload.delta;
                onDelta(payload.delta);
            }
        }
    }
    if (!full.trim()) {
        throw new MeetingAiApiError(response.status, 'empty reply');
    }
    return full;
}
export { AI_NOTES_FEATURE_KEY, AI_NOTES_HTTP_CONFLICT, AI_NOTES_HTTP_NOT_FOUND, AI_NOTES_SUMMARY_MODE_FINAL, AI_NOTES_SUMMARY_MODE_PARTIAL, MS_PER_SECOND, REC_SUMMARY_POLL_INTERVAL_MS, REC_SUMMARY_POLL_MAX_ATTEMPTS, REC_SUMMARY_STATUS_FAILED, REC_SUMMARY_STATUS_PENDING, REC_SUMMARY_STATUS_PROCESSING, REC_SUMMARY_STATUS_READY };
